import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Stack,
  useTheme
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import PageLayout from './PageLayout';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getContrastText } from '../utils/colorUtils';

export default function Pomodoro({ customColors }) {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const timerRef = useRef(null);

  const [time, setTime] = useState(25 * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [settings, setSettings] = useState({
    workDuration: 25,
    breakDuration: 5,
    longBreakDuration: 30,
    sessionsUntilLongBreak: 4,
    workSeconds: 0,
    breakSeconds: 0,
    longBreakSeconds: 0
  });
  const [openSettings, setOpenSettings] = useState(false);
  const [currentSession, setCurrentSession] = useState(1);
  const [isBreak, setIsBreak] = useState(false);
  const [openPomodoroHelp, setOpenPomodoroHelp] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [nextTimer, setNextTimer] = useState(null);
  const [isCompletingTimer, setIsCompletingTimer] = useState(false);

  // Carrega as configurações salvas ao iniciar
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem('pomodoroSettings');
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(prev => ({
          ...prev,
          ...parsedSettings
        }));
        
        // Configura o timer inicial com as configurações salvas
        const initialMinutes = parsedSettings.workDuration;
        const initialSeconds = parsedSettings.workSeconds;
        setTime((initialMinutes * 60) + initialSeconds);
      }
    } catch (error) {
      console.error('Erro ao carregar configurações do Pomodoro:', error);
    }
  }, []);

  // Salva as configurações quando elas mudam
  useEffect(() => {
    try {
      localStorage.setItem('pomodoroSettings', JSON.stringify(settings));
    } catch (error) {
      console.error('Erro ao salvar configurações do Pomodoro:', error);
    }
  }, [settings]);

  useEffect(() => {
    loadUserSettings();
    // Request notification permission
    if ("Notification" in window) {
      Notification.requestPermission();
    }

    // Adicionar event listener para visibilidade da página
    const handleVisibilityChange = () => {
      if (!currentUser) {
        setTime(0);
        setIsRunning(false);
        setIsBreak(false);
        setCurrentSession(1);
        setSessionHistory([]);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser]);

  // Efeito para atualizar o timer quando nextTimer mudar
  useEffect(() => {
    if (nextTimer !== null) {
      setTime(nextTimer);
      setNextTimer(null);
    }
  }, [nextTimer]);

  const loadUserSettings = async () => {
    if (!currentUser) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.pomodoroSettings) {
          setSettings(userData.pomodoroSettings);
          const initialMinutes = userData.pomodoroSettings.workDuration;
          const initialSeconds = userData.pomodoroSettings.workSeconds;
          setTime((initialMinutes * 60) + initialSeconds);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const saveUserSettings = async (newSettings) => {
    if (!currentUser) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        pomodoroSettings: newSettings || settings
      });
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDuration = (minutes, seconds) => {
    if (minutes === 0) {
      return `${seconds}s`;
    } else if (seconds === 0) {
      return `${minutes}min`;
    }
    return `${minutes}min ${seconds}s`;
  };

  const handleTimerComplete = () => {
    try {
      // Se já está completando, ignora completamente
      if (isCompletingTimer) {
        console.log('Timer já está completando, ignorando chamada duplicada');
        return;
      }

      // Marca que está iniciando o processo de completar
      setIsCompletingTimer(true);
      console.log('Iniciando completion do timer');

      // Para o timer atual
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setIsRunning(false);

      // Captura o estado atual antes de qualquer mudança
      const currentPeriod = {
        session: currentSession,
        isBreak: isBreak,
        type: isBreak 
          ? (currentSession % settings.sessionsUntilLongBreak === 0 ? 'pausa longa' : 'pausa curta')
          : 'foco',
        duration: isBreak 
          ? (currentSession % settings.sessionsUntilLongBreak === 0 
              ? settings.longBreakDuration 
              : settings.breakDuration)
          : settings.workDuration,
        seconds: isBreak 
          ? (currentSession % settings.sessionsUntilLongBreak === 0 
              ? settings.longBreakSeconds 
              : settings.breakSeconds)
          : settings.workSeconds,
        timestamp: Date.now()
      };

      // Registra o período atual e remove duplicatas depois
      setSessionHistory(prev => {
        try {
          // Adiciona o novo período
          const newHistory = [...prev, currentPeriod];
          
          // Remove duplicatas mantendo apenas a primeira ocorrência de cada período
          const uniqueHistory = newHistory.reduce((acc, current) => {
            const isDuplicate = acc.some(item => 
              item.type === current.type && 
              item.session === current.session &&
              item.isBreak === current.isBreak
            );
            if (!isDuplicate) {
              acc.push(current);
            }
            return acc;
          }, []);

          return uniqueHistory;
        } catch (error) {
          console.error('Erro ao atualizar histórico:', error);
          return prev;
        }
      });

      // Calcula e atualiza o próximo estado
      const nextIsBreak = !currentPeriod.isBreak;
      const nextSession = currentPeriod.isBreak 
        ? (currentSession + 1) // Sempre incrementa a sessão após uma pausa
        : currentSession;

      const nextMinutes = nextIsBreak
        ? (nextSession % settings.sessionsUntilLongBreak === 0 
            ? settings.longBreakDuration
            : settings.breakDuration)
        : settings.workDuration;

      const nextSeconds = nextIsBreak
        ? (nextSession % settings.sessionsUntilLongBreak === 0 
            ? settings.longBreakSeconds
            : settings.breakSeconds)
        : settings.workSeconds;

      // Atualiza os estados para o próximo período
      setIsBreak(nextIsBreak);
      setCurrentSession(nextSession);
      setTime((nextMinutes * 60) + nextSeconds);

      // Notificações
      if (Notification.permission === "granted") {
        try {
          new Notification("Pomodoro Timer", {
            body: currentPeriod.isBreak ? "Intervalo terminado! Hora de voltar ao trabalho!" : "Tempo terminado! Hora do intervalo!",
            icon: "/favicon.ico"
          });
        } catch (error) {
          console.error('Erro ao mostrar notificação:', error);
        }
      }

      // Som
      try {
        const audio = new Audio('/sounds/notification.mp3');
        audio.play().catch(error => {
          console.log('Erro ao reproduzir o áudio:', error);
        });
      } catch (error) {
        console.log('Erro ao criar o objeto de áudio:', error);
      }

      // Reseta a flag de completion
      setIsCompletingTimer(false);
    } catch (error) {
      console.error('Erro ao completar timer:', error);
      // Em caso de erro, garante que os estados são resetados
      setIsCompletingTimer(false);
      setIsRunning(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const handleStart = () => {
    try {
      // Limpa qualquer timer existente
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Se o timer estiver em 0, configura o tempo inicial
      if (time <= 0) {
        const initialMinutes = isBreak 
          ? (currentSession % settings.sessionsUntilLongBreak === 0
              ? settings.longBreakDuration
              : settings.breakDuration)
          : settings.workDuration;
        
        const initialSeconds = isBreak 
          ? (currentSession % settings.sessionsUntilLongBreak === 0
              ? settings.longBreakSeconds
              : settings.breakSeconds)
          : settings.workSeconds;

        setTime((initialMinutes * 60) + initialSeconds);
      }
      
      // Atualiza o estado de execução
      setIsRunning(true);

      // Cria um novo intervalo
      timerRef.current = setInterval(() => {
        setTime(prevTime => {
          try {
            if (prevTime <= 1) {
              // Limpa o intervalo antes de chamar handleTimerComplete
              if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
              }
              // Chama handleTimerComplete de forma segura
              setTimeout(() => handleTimerComplete(), 0);
              return 0;
            }
            return prevTime - 1;
          } catch (error) {
            console.error('Erro ao atualizar timer:', error);
            // Em caso de erro, para o timer e retorna o valor atual
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            setIsRunning(false);
            return prevTime;
          }
        });
      }, 1000);
    } catch (error) {
      console.error('Erro ao iniciar timer:', error);
      // Em caso de erro, garante que o timer está parado
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setIsRunning(false);
    }
  };

  const handlePause = () => {
    setIsRunning(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleStop = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRunning(false);
    resetTimer();
  };

  const resetTimer = () => {
    const initialMinutes = settings.workDuration;
    const initialSeconds = settings.workSeconds;
    setTime((initialMinutes * 60) + initialSeconds);
    setIsBreak(false);
    setCurrentSession(1);
    setSessionHistory([]);
  };

  const handleSettingsSave = () => {
    try {
      // Salva as configurações
      localStorage.setItem('pomodoroSettings', JSON.stringify(settings));
      saveUserSettings(settings);
      
      // Atualiza o timer com as novas configurações
      const initialMinutes = settings.workDuration;
      const initialSeconds = settings.workSeconds;
      setTime((initialMinutes * 60) + initialSeconds);
      
      // Reseta o estado do timer
      setIsBreak(false);
      setCurrentSession(1);
      setSessionHistory([]);
      
      setOpenSettings(false);
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
    }
  };

  return (
    <PageLayout
      title="Pomodoro"
      hideAddButton
      customColors={customColors}
    >
      {/* Seção do Pomodoro */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3} alignItems="flex-start">
          {/* Pomodoro Timer Section - Full width */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2 
                }}>
                  <Typography variant="h6" component="div">
                    Timer Pomodoro
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      onClick={() => setOpenPomodoroHelp(true)}
                      color="primary"
                      size="small"
                    >
                      <HelpIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => setOpenSettings(true)}
                      color="primary"
                      size="small"
                    >
                      <SettingsIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Configurações atuais */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Configurações Atuais:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2">
                        🎯 Foco: {formatDuration(settings.workDuration, settings.workSeconds)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2">
                        ☕ Pausa Curta: {formatDuration(settings.breakDuration, settings.breakSeconds)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2">
                        🌟 Pausa Longa: {formatDuration(settings.longBreakDuration, settings.longBreakSeconds)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2">
                        📍 Sessões até pausa longa: {settings.sessionsUntilLongBreak}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Timer Display */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ 
                    color: isBreak 
                      ? (currentSession % settings.sessionsUntilLongBreak === 0 
                        ? 'warning.main' // Cor laranja para pausa longa
                        : 'success.main') // Cor verde para pausa curta
                      : (customColors?.primary || 'primary.main'), // Cor primária para foco
                    mb: 1
                  }}>
                    {isBreak 
                      ? (currentSession % settings.sessionsUntilLongBreak === 0 
                        ? `Sessão ${currentSession.toString().padStart(2, '0')} - Pausa Longa`
                        : `Sessão ${currentSession.toString().padStart(2, '0')} - Pausa Curta`)
                      : `Sessão ${currentSession.toString().padStart(2, '0')} - Foco`}
                  </Typography>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {formatTime(time)}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: isRunning ? 'success.main' : 'text.secondary',
                    mt: 1,
                    mb: 2
                  }}>
                    Status: {isRunning ? 'Em andamento' : 'Aguardando'}
                  </Typography>

                  {/* Botões de Controle */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                    <IconButton
                      onClick={isRunning ? handlePause : handleStart}
                      sx={{
                        bgcolor: customColors?.primary || 'primary.main',
                        color: customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText,
                        width: 56,
                        height: 56,
                        '&:hover': {
                          bgcolor: customColors?.primary || 'primary.main',
                          opacity: 0.9
                        }
                      }}
                    >
                      {isRunning ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                    <IconButton
                      onClick={handleStop}
                      sx={{
                        bgcolor: 'error.main',
                        color: 'white',
                        width: 56,
                        height: 56,
                        '&:hover': {
                          bgcolor: 'error.dark',
                        }
                      }}
                    >
                      <StopIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Histórico de Sessões */}
                {sessionHistory.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                      Histórico da Sessão Atual:
                    </Typography>
                    <Stack spacing={1}>
                      {sessionHistory.map((session, index) => (
                        <Box 
                          key={index}
                          sx={{ 
                            p: 1, 
                            bgcolor: 'background.default',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <Typography variant="body2" sx={{ 
                            color: session.type === 'foco' 
                              ? (customColors?.primary || 'primary.main')
                              : (session.type === 'pausa longa' 
                                ? 'warning.main' // Cor laranja para pausa longa
                                : 'success.main') // Cor verde para pausa curta
                          }}>
                            {session.type === 'pausa longa' 
                              ? 'Pausa Longa'
                              : `Sessão ${session.session.toString().padStart(2, '0')} - ${session.type}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({formatDuration(session.duration, session.seconds)})
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Settings Dialog */}
      <Dialog 
        open={openSettings} 
        onClose={() => setOpenSettings(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Configurações do Pomodoro</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Tempo de Trabalho */}
            <Box>
              <Typography gutterBottom>
                Tempo de Trabalho: {settings.workDuration}min {settings.workSeconds}s
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Minutos</Typography>
                  <Slider
                    value={settings.workDuration}
                    onChange={(e, value) => setSettings(prev => ({ ...prev, workDuration: value }))}
                    min={0}
                    max={60}
                    marks={[
                      { value: 25, label: '25' },
                      { value: 45, label: '45' }
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Segundos</Typography>
                  <Slider
                    value={settings.workSeconds}
                    onChange={(e, value) => setSettings(prev => ({ ...prev, workSeconds: value }))}
                    min={0}
                    max={59}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 30, label: '30' }
                    ]}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Tempo de Pausa */}
            <Box>
              <Typography gutterBottom>
                Tempo de Pausa: {settings.breakDuration}min {settings.breakSeconds}s
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Minutos</Typography>
                  <Slider
                    value={settings.breakDuration}
                    onChange={(e, value) => setSettings(prev => ({ ...prev, breakDuration: value }))}
                    min={0}
                    max={30}
                    marks={[
                      { value: 5, label: '5' },
                      { value: 15, label: '15' }
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Segundos</Typography>
                  <Slider
                    value={settings.breakSeconds}
                    onChange={(e, value) => setSettings(prev => ({ ...prev, breakSeconds: value }))}
                    min={0}
                    max={59}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 30, label: '30' }
                    ]}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Tempo de Pausa Longa */}
            <Box>
              <Typography gutterBottom>
                Tempo de Pausa Longa: {settings.longBreakDuration}min {settings.longBreakSeconds}s
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Minutos</Typography>
                  <Slider
                    value={settings.longBreakDuration}
                    onChange={(e, value) => setSettings(prev => ({ ...prev, longBreakDuration: value }))}
                    min={0}
                    max={60}
                    marks={[
                      { value: 15, label: '15' },
                      { value: 30, label: '30' }
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Segundos</Typography>
                  <Slider
                    value={settings.longBreakSeconds}
                    onChange={(e, value) => setSettings(prev => ({ ...prev, longBreakSeconds: value }))}
                    min={0}
                    max={59}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 30, label: '30' }
                    ]}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Sessões até Pausa Longa */}
            <Box>
              <Typography gutterBottom>
                Sessões até Pausa Longa: {settings.sessionsUntilLongBreak}
              </Typography>
              <Slider
                value={settings.sessionsUntilLongBreak}
                onChange={(e, value) => setSettings(prev => ({ ...prev, sessionsUntilLongBreak: value }))}
                min={1}
                max={8}
                marks={[
                  { value: 4, label: '4' },
                  { value: 6, label: '6' }
                ]}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSettings(false)}>Cancelar</Button>
          <Button 
            onClick={handleSettingsSave} 
            variant="contained"
            sx={{
              bgcolor: customColors?.primary || 'primary.main',
              color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
              '&:hover': {
                bgcolor: customColors?.primary || 'primary.main',
                opacity: 0.9
              }
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={openPomodoroHelp} onClose={() => setOpenPomodoroHelp(false)}>
        <DialogTitle>Como funciona o Timer Pomodoro?</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            A Técnica <b>Pomodoro</b> é um método de gerenciamento de tempo que ajuda você a manter o foco e a produtividade. Funciona assim:
          </Typography>
          <Typography component="div" sx={{ mb: 2 }}>
            <ul>
              <li>Trabalhe focado por 25 minutos (um "pomodoro")</li>
              <li>Faça uma pausa curta de 5 minutos</li>
              <li>Após 4 pomodoros, faça uma pausa longa de 15 minutos</li>
              <li>Repita o ciclo</li>
            </ul>
          </Typography>
          <Typography paragraph>
            Benefícios:
          </Typography>
          <Typography component="div">
            <ul>
              <li>Melhora o foco e a concentração</li>
              <li>Reduz a fadiga mental</li>
              <li>Aumenta a produtividade</li>
              <li>Ajuda a evitar procrastinação</li>
              <li>Mantém sua mente fresca com pausas regulares</li>
            </ul>
          </Typography>
          <Typography sx={{ mt: 2 }}>
        <b>Você pode personalizar os tempos de trabalho e pausa</b> nas configurações do timer para se adequar melhor ao seu ritmo!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPomodoroHelp(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}


